import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { extendLoansTheme } from '../features/loans/theme/loansTokens';
import { allThemes } from '../design-system/theme/themes';
import type { ThemeDefinition } from '../design-system/theme/themes';

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  /** Backward-compatible mode derived from the active theme */
  theme: ThemeMode;
  /** Toggle between light/dark variant of the current theme */
  toggleTheme: () => void;
  /** Convenience flag */
  isDark: boolean;
  /** Full definition of the active theme */
  currentTheme: ThemeDefinition;
  /** Every registered theme (light + dark variants) */
  availableThemes: ThemeDefinition[];
  /** Switch to a theme by its id */
  setTheme: (themeId: string) => void;
  /** Active theme id */
  themeId: string;
}

const STORAGE_KEY = 'koodooka-theme';
const LEGACY_STORAGE_KEY = 'theme';
const LEGACY_STORAGE_KEY_ALT = 'theme-mode';
const DEFAULT_THEME_ID = 'koodooka';
const LEGACY_DEFAULT_LIGHT_THEME_ID = 'modern-minimal';
const LEGACY_DEFAULT_DARK_THEME_ID = 'modern-minimal-dark';

function findTheme(id: string): ThemeDefinition {
  return allThemes.find((t) => t.id === id) ?? allThemes.find((t) => t.id === DEFAULT_THEME_ID)!;
}

function resolveInitialThemeId(): string {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    const migratedSaved =
      saved === LEGACY_DEFAULT_LIGHT_THEME_ID
        ? DEFAULT_THEME_ID
        : saved === LEGACY_DEFAULT_DARK_THEME_ID
          ? 'koodooka-dark'
          : saved;
    if (allThemes.some((t) => t.id === migratedSaved)) {
      if (migratedSaved !== saved) localStorage.setItem(STORAGE_KEY, migratedSaved);
      return migratedSaved;
    }
  }

  // Legacy migration
  const legacy =
    localStorage.getItem(LEGACY_STORAGE_KEY_ALT) ?? localStorage.getItem(LEGACY_STORAGE_KEY);
  if (legacy === 'dark') {
    localStorage.setItem(STORAGE_KEY, 'koodooka-dark');
    return 'koodooka-dark';
  }
  if (legacy === 'light') {
    localStorage.setItem(STORAGE_KEY, DEFAULT_THEME_ID);
    return DEFAULT_THEME_ID;
  }

  return DEFAULT_THEME_ID;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeId, setThemeId] = useState<string>(resolveInitialThemeId);

  const currentTheme = findTheme(themeId);
  const isDark = currentTheme.mode === 'dark';
  const theme: ThemeMode = isDark ? 'dark' : 'light';

  const setTheme = useCallback((id: string) => {
    if (allThemes.some((t) => t.id === id)) {
      setThemeId(id);
      localStorage.setItem(STORAGE_KEY, id);
    }
  }, []);

  const toggleTheme = useCallback((): void => {
    const baseId = themeId.replace(/-dark$/, '');
    const newId = isDark ? baseId : `${baseId}-dark`;
    setTheme(newId);
  }, [themeId, isDark, setTheme]);

  useEffect(() => {
    document.body.className = `theme-${theme}`;
  }, [theme]);

  const algorithm = isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

  const themeConfig = extendLoansTheme(
    {
      algorithm: [algorithm],
      token: {
        colorPrimary: currentTheme.tokens.colorPrimary,
        colorLink: currentTheme.tokens.colorPrimary,
      },
    },
    theme,
  );

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        isDark,
        currentTheme,
        availableThemes: allThemes,
        setTheme,
        themeId,
      }}
    >
      <ConfigProvider
        theme={themeConfig}
        direction={document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr'}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
